import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { FullScreenDialog } from '@/components/ui/full-screen-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import OrganizationChart from 'components/IncidentConfig/OrganizationChart';
import { IncidentScopeProvider } from 'components/IAP/hooks/incidentScope';
import { useGetRosterForIncident } from 'hooks/incident/useRoster';
import { useEnsurePlanTemplateIncident } from 'hooks/planLibrary/planLibraryHooks';
import {
  RosterDraftProvider,
  useCreateRosterDraft,
} from 'components/IncidentConfig/CreateIncident/CreateRosterDraftContext';
import type {
  DraftRosterAssignment,
  DraftRosterSeat,
} from 'components/IncidentConfig/CreateIncident/createIncidentModel';
import { orgTemplateIdFromSlug } from 'components/IncidentConfig/roster/templates/rosterTemplateCatalog';
import type { RosterTemplateNodeDto } from 'components/IncidentConfig/roster/templates/rosterTemplateTypes';
import { useCreateRosterTemplate } from 'hooks/rosterTemplateLibrary/rosterTemplateLibraryHooks';
import {
  useSaveFilledRosterTemplate,
  type FilledRosterResourceLink,
  type FilledRosterTemplate,
} from 'hooks/rosterTemplateLibrary/filledRosterTemplateHooks';
import { selectGroupGuid } from 'slices/commonSelectors';
import { useHwcgFlag } from 'utils/featureFlags';
import {
  draftAssignmentsFromFilled,
  filledAssignmentsFromDraft,
} from '../RosterTemplatesTab/filledRosterAssignments';
import {
  duplicateSeatNames,
  nodesFromSeats,
  seatsFromNodes,
} from '../RosterTemplatesTab/rosterTemplateSeats';

const NO_ASSIGNMENTS: DraftRosterAssignment[] = [];

type TemplateResource = FilledRosterResourceLink & { positionId: string };

type PendingFill =
  | {
      kind: 'people';
      seats: DraftRosterSeat[];
      assignments: DraftRosterAssignment[];
    }
  | { kind: 'resource'; resource: TemplateResource };

function assignmentWasAdded(
  previous: DraftRosterAssignment[],
  next: DraftRosterAssignment[]
) {
  return next.some(
    (row) =>
      !previous.some(
        (prior) =>
          prior.positionId === row.positionId &&
          prior.user_guid === row.user_guid
      )
  );
}

export type PlanRosterDialogSeed = {
  existingFilled?: FilledRosterTemplate | null;
  /** Structure nodes for the filled template's source chart. */
  structureNodes?: RosterTemplateNodeDto[];
  templateName?: string;
  sourceTemplateId?: string | null;
  sourceSlug?: string | null;
};

/**
 * The roster chart with its Templates panel, for choosing a plan's roster
 * templates. Loading a saved organization template adds it to the plan.
 * Assigning a person or asset on a structure template confirms that it becomes
 * filled. With HWCG on, the plan roster is the template workspace org chart.
 */
export function PlanRosterDialog({
  planId,
  planName,
  onAddTemplate,
  onFilledTemplate,
  seed,
  onClose,
}: {
  planId: string;
  planName: string;
  onAddTemplate: (templateId: string) => void;
  /** HWCG: called after a filled roster is saved so the plan can link it. */
  onFilledTemplate?: (templateId: string) => void;
  seed?: PlanRosterDialogSeed;
  onClose: () => void;
}) {
  const hwcg = useHwcgFlag();
  if (hwcg) {
    return (
      <PlanWorkspaceRosterDialog
        planId={planId}
        planName={planName}
        onClose={onClose}
      />
    );
  }
  return (
    <PlanRosterDraftDialog
      planName={planName}
      onAddTemplate={onAddTemplate}
      onFilledTemplate={onFilledTemplate}
      seed={seed}
      onClose={onClose}
    />
  );
}

function PlanRosterDraftDialog({
  planName,
  onAddTemplate,
  onFilledTemplate,
  seed,
  onClose,
}: {
  planName: string;
  onAddTemplate: (templateId: string) => void;
  onFilledTemplate?: (templateId: string) => void;
  seed?: PlanRosterDialogSeed;
  onClose: () => void;
}) {
  const hwcg = useHwcgFlag();
  const groupGuid = useSelector(selectGroupGuid);
  const createTemplate = useCreateRosterTemplate();
  const saveFilled = useSaveFilledRosterTemplate();
  const existingFilled = seed?.existingFilled ?? null;

  const initialSeats = seed?.structureNodes?.length
    ? seatsFromNodes(seed.structureNodes)
    : [];
  const [seats, setSeats] = useState<DraftRosterSeat[]>(initialSeats);
  const [assignments, setAssignments] = useState<DraftRosterAssignment[]>(
    () => {
      if (!existingFilled || !seed?.structureNodes?.length) return [];
      return draftAssignmentsFromFilled(
        seatsFromNodes(seed.structureNodes),
        existingFilled.assignments
      );
    }
  );
  const [resources, setResources] = useState<TemplateResource[]>(() => {
    const idByName = new Map(
      initialSeats.map((seat) => [seat.name.trim(), seat.id])
    );
    return (existingFilled?.resources ?? []).flatMap((resource) => {
      const positionId = idByName.get(resource.seatName.trim());
      if (!positionId) return [];
      return [{ ...resource, positionId }];
    });
  });
  const [name, setName] = useState(
    existingFilled?.name ?? seed?.templateName ?? ''
  );
  const [description, setDescription] = useState('');
  const [sourceTemplateId, setSourceTemplateId] = useState<string | null>(
    existingFilled?.sourceTemplateId ?? seed?.sourceTemplateId ?? null
  );
  const [sourceSlug, setSourceSlug] = useState<string | null>(
    existingFilled?.sourceSlug ?? seed?.sourceSlug ?? null
  );
  const [pendingFill, setPendingFill] = useState<PendingFill | null>(null);
  const filledCommitted = useRef(Boolean(existingFilled));
  const filledIdRef = useRef<string | undefined>(existingFilled?.id);

  const saveFilledSnapshot = useCallback(
    (
      nextSeats: DraftRosterSeat[],
      nextAssignments: DraftRosterAssignment[],
      nextResources: TemplateResource[],
      source: { sourceTemplateId: string | null; sourceSlug: string | null }
    ) => {
      if (!hwcg || !onFilledTemplate) return;
      const templateName =
        name.trim() || existingFilled?.name || `${planName} roster`;
      if (!name.trim()) setName(templateName);
      const resourceLinks = nextResources.map((resource) => ({
        seatName:
          nextSeats
            .find((seat) => seat.id === resource.positionId)
            ?.name.trim() || resource.seatName,
        resourceSetId: resource.resourceSetId,
        name: resource.name,
      }));
      const write = (nextSource: typeof source) => {
        saveFilled.mutate(
          {
            id: filledIdRef.current,
            name: templateName,
            sourceTemplateId: nextSource.sourceTemplateId,
            sourceSlug: nextSource.sourceSlug,
            assignments: filledAssignmentsFromDraft(nextSeats, nextAssignments),
            resources: resourceLinks,
          },
          {
            onSuccess: (created) => {
              filledIdRef.current = created.id;
              onFilledTemplate(created.id);
            },
          }
        );
      };
      if (source.sourceTemplateId || source.sourceSlug) {
        write(source);
        return;
      }
      createTemplate.mutate(
        {
          name: templateName,
          description: description.trim() || null,
          attachOnly: false,
          nodes: nodesFromSeats(nextSeats),
        },
        {
          onSuccess: (created) => {
            onAddTemplate(created.id);
            const nextSource = {
              sourceTemplateId: created.id,
              sourceSlug: `org:${created.id}`,
            };
            setSourceTemplateId(nextSource.sourceTemplateId);
            setSourceSlug(nextSource.sourceSlug);
            write(nextSource);
          },
        }
      );
    },
    [
      createTemplate,
      description,
      existingFilled?.name,
      hwcg,
      name,
      onAddTemplate,
      onFilledTemplate,
      planName,
      saveFilled,
    ]
  );

  const handleChange = useCallback(
    (
      nextSeats: DraftRosterSeat[],
      nextAssignments: DraftRosterAssignment[]
    ) => {
      const addingPerson =
        hwcg &&
        !filledCommitted.current &&
        assignmentWasAdded(assignments, nextAssignments);
      if (addingPerson) {
        setPendingFill({
          kind: 'people',
          seats: nextSeats,
          assignments: nextAssignments,
        });
        return;
      }
      setSeats(nextSeats);
      setAssignments(nextAssignments);
      if (
        hwcg &&
        filledCommitted.current &&
        assignmentWasAdded(assignments, nextAssignments)
      ) {
        saveFilledSnapshot(nextSeats, nextAssignments, resources, {
          sourceTemplateId,
          sourceSlug,
        });
      }
    },
    [
      assignments,
      hwcg,
      resources,
      saveFilledSnapshot,
      sourceSlug,
      sourceTemplateId,
    ]
  );

  const requestResource = useCallback(
    (resource: { positionId: string; resourceSetId: string; name: string }) => {
      const seatName =
        seats.find((seat) => seat.id === resource.positionId)?.name.trim() ||
        '';
      const next = { ...resource, seatName };
      if (hwcg && !filledCommitted.current) {
        setPendingFill({ kind: 'resource', resource: next });
        return;
      }
      const nextResources = [...resources, next];
      setResources(nextResources);
      if (hwcg && filledCommitted.current) {
        saveFilledSnapshot(seats, assignments, nextResources, {
          sourceTemplateId,
          sourceSlug,
        });
      }
    },
    [
      assignments,
      hwcg,
      resources,
      saveFilledSnapshot,
      seats,
      sourceSlug,
      sourceTemplateId,
    ]
  );

  const acceptFill = () => {
    if (!pendingFill) return;
    filledCommitted.current = true;
    if (pendingFill.kind === 'people') {
      setSeats(pendingFill.seats);
      setAssignments(pendingFill.assignments);
      saveFilledSnapshot(
        pendingFill.seats,
        pendingFill.assignments,
        resources,
        { sourceTemplateId, sourceSlug }
      );
    } else {
      const nextResources = [...resources, pendingFill.resource];
      setResources(nextResources);
      saveFilledSnapshot(seats, assignments, nextResources, {
        sourceTemplateId,
        sourceSlug,
      });
    }
    setPendingFill(null);
  };

  const handleTemplateLoaded = useCallback(
    (slug: string) => {
      const templateId = orgTemplateIdFromSlug(slug);
      setSourceSlug(slug);
      setSourceTemplateId(templateId);
      if (templateId) onAddTemplate(templateId);
    },
    [onAddTemplate]
  );

  const duplicates = useMemo(() => duplicateSeatNames(seats), [seats]);
  const hasBlankSeat = seats.some((seat) => !seat.name.trim());
  const canSaveStructure =
    !!name.trim() &&
    seats.length > 0 &&
    !hasBlankSeat &&
    duplicates.length === 0 &&
    !createTemplate.isPending;

  const startNewRoster = () => {
    setSeats([]);
    setAssignments([]);
    setResources([]);
    setName('');
    setDescription('');
    setSourceTemplateId(null);
    setSourceSlug(null);
    filledIdRef.current = undefined;
    filledCommitted.current = false;
  };

  const saveAsTemplate = () => {
    createTemplate.mutate(
      {
        name: name.trim(),
        description: description.trim() || null,
        attachOnly: false,
        nodes: nodesFromSeats(seats),
      },
      {
        onSuccess: (created) => {
          onAddTemplate(created.id);
          setSourceTemplateId(created.id);
          setSourceSlug(`org:${created.id}`);
        },
      }
    );
  };

  if (!groupGuid) return null;

  return (
    <RosterDraftProvider
      groupGuid={groupGuid}
      seats={seats}
      assignments={hwcg ? assignments : NO_ASSIGNMENTS}
      creator={null}
      structureOnly
      allowAssign={hwcg}
      templateAssignment
      templateName={name}
      templateResources={resources}
      onAddTemplateResource={requestResource}
      onChange={handleChange}
      onTemplateLoaded={handleTemplateLoaded}
    >
      <FullScreenDialog
        open
        onOpenChange={(next) => {
          if (!next) onClose();
        }}
        title={`Roster templates for ${planName}`}
        description={
          hwcg
            ? 'Load a saved template from the Templates panel to add it to this plan, or build a chart and save it as a template. Assign a person or asset on a seat to make it a filled template.'
            : 'Load a saved template from the Templates panel to add it to this plan, or build a chart and save it as a template.'
        }
        bodyClassName="tw:!flex tw:!min-h-0 tw:!flex-col tw:!overflow-hidden tw:!p-3"
        header={
          <div className="tw:grid tw:gap-3 tw:md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] tw:md:items-end">
            <div className="tw:flex tw:flex-col tw:gap-1">
              <Label htmlFor="plan-roster-template-name">Template name</Label>
              <Input
                id="plan-roster-template-name"
                value={name}
                maxLength={200}
                onChange={(event) => setName(event.target.value)}
              />
            </div>
            <div className="tw:flex tw:flex-col tw:gap-1">
              <Label htmlFor="plan-roster-template-description">
                Description
              </Label>
              <Textarea
                id="plan-roster-template-description"
                value={description}
                rows={1}
                className="tw:min-h-9 tw:resize-none"
                onChange={(event) => setDescription(event.target.value)}
              />
            </div>
          </div>
        }
        footer={
          <>
            {duplicates.length > 0 ? (
              <p className="tw:mr-auto tw:m-0 tw:text-xs tw:text-destructive">
                Seat names must be unique. Rename: {duplicates.join(', ')}
              </p>
            ) : hasBlankSeat ? (
              <p className="tw:mr-auto tw:m-0 tw:text-xs tw:text-destructive">
                Every seat needs a name.
              </p>
            ) : null}
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={createTemplate.isPending || saveFilled.isPending}
              onClick={startNewRoster}
            >
              New roster
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!canSaveStructure}
              onClick={saveAsTemplate}
            >
              {createTemplate.isPending ? 'Saving…' : 'Save as template'}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={onClose}>
              Done
            </Button>
          </>
        }
      >
        <PlanRosterChartBody showAssignees={hwcg} />
      </FullScreenDialog>
      <AlertDialog
        open={pendingFill !== null}
        onOpenChange={(next) => {
          if (!next) setPendingFill(null);
        }}
      >
        <AlertDialogContent className="dark">
          <AlertDialogHeader>
            <AlertDialogTitle>Make this a filled template?</AlertDialogTitle>
            <AlertDialogDescription>
              Assigning a person or asset links them to this template. It
              becomes a filled template, and later assignments are saved with
              it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={acceptFill}>
              Make filled template
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </RosterDraftProvider>
  );
}

/**
 * The plan's template incident, so the seat modal is the incident modal: Team,
 * Activation, Planning, and Execution stay on that workspace.
 */
function PlanWorkspaceRosterDialog({
  planId,
  planName,
  onClose,
}: {
  planId: string;
  planName: string;
  onClose: () => void;
}) {
  const ensure = useEnsurePlanTemplateIncident();
  const ensureRoster = ensure.mutate;
  const [templateIncidentId, setTemplateIncidentId] = useState<string | null>(
    null
  );

  useEffect(() => {
    ensureRoster(
      { planId },
      {
        onSuccess: (incidentId) => setTemplateIncidentId(incidentId),
      }
    );
  }, [ensureRoster, planId]);

  return (
    <FullScreenDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={`Roster for ${planName}`}
      description="Seats, people, activation, and phase tasks are saved on this plan's workspace and copied when the plan is applied."
      bodyClassName="tw:!flex tw:!min-h-0 tw:!flex-col tw:!overflow-hidden tw:!p-3"
      footer={
        <Button type="button" size="sm" variant="outline" onClick={onClose}>
          Done
        </Button>
      }
    >
      {templateIncidentId ? (
        <IncidentScopeProvider incidentId={templateIncidentId}>
          <PlanWorkspaceChart />
        </IncidentScopeProvider>
      ) : (
        <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
          Preparing the plan roster…
        </p>
      )}
    </FullScreenDialog>
  );
}

function PlanWorkspaceChart() {
  const { data: rosterData = [] } = useGetRosterForIncident();
  return (
    <div className="tw:min-h-0 tw:flex-1 tw:overflow-auto">
      <OrganizationChart incidentMembers={rosterData} />
    </div>
  );
}

function PlanRosterChartBody({ showAssignees }: { showAssignees: boolean }) {
  const { members } = useCreateRosterDraft();
  return (
    <div className="tw:min-h-0 tw:flex-1 tw:overflow-auto">
      <OrganizationChart incidentMembers={showAssignees ? members : []} />
    </div>
  );
}
